/**
 * Customer Account delivery-core — browser mirror (Slice 09+).
 * Keep region helpers in sync with lib/customer-account/delivery-core.js
 */
(function (global) {
  'use strict';

  var ROUTE_DIRECTION_MAP = {
    C: 'Cambridge',
    E: 'Mississauga',
    N: 'North York',
    O: 'Oakville',
    W: 'Hamilton'
  };

  function getRouteDirection(routeLabel) {
    if (!routeLabel) return '';
    var prefix = String(routeLabel).trim().charAt(0).toUpperCase();
    return ROUTE_DIRECTION_MAP[prefix] || '';
  }

  function resolveRegion(route) {
    if (!route) return '';
    return route.region || getRouteDirection(route.routeLabel);
  }

  /**
   * Spot availability — prefer server routes[].spotAvailable (v1.2 / production).
   * Only planned routes can have open spots; started / completed routes are always closed.
   * Do NOT recompute from estimatedReturnAt on the client (backend handoff).
   */
  function isSpotAvailable(route) {
    if (!route) return false;
    if (route.routeStatus !== 'planned') return false;
    return route.spotAvailable === true;
  }

  var ROUTE_STATUS_SORT_ORDER = { in_progress: 0, planned: 1, completed: 2 };

  function routeStatusSortKey(status) {
    if (Object.prototype.hasOwnProperty.call(ROUTE_STATUS_SORT_ORDER, status)) {
      return ROUTE_STATUS_SORT_ORDER[status];
    }
    return 1;
  }

  function sortRoutesByStatus(routes) {
    var allRoutes = (routes || []).slice();
    allRoutes.sort(function (a, b) {
      return routeStatusSortKey(a.routeStatus) - routeStatusSortKey(b.routeStatus);
    });
    return allRoutes;
  }

  function formatRouteEta(iso) {
    if (!iso) return '';
    var t = new Date(iso);
    if (isNaN(t.getTime())) return '';
    var h = t.getHours();
    var m = t.getMinutes();
    var ampm = h >= 12 ? 'PM' : 'AM';
    var hh = h % 12 || 12;
    var mm = m < 10 ? '0' + m : String(m);
    return hh + ':' + mm + ' ' + ampm;
  }

  /** Locale-aware delivered timestamp for order cards. */
  function formatDeliveredAt(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return iso;
    }
  }

  var SCHEDULED_WINDOW_I18N_KEYS = {
    morning: 'window-morning',
    afternoon: 'window-afternoon',
    evening: 'window-evening'
  };

  var ROUTE_STATUS_FALLBACK_LABELS = {
    planned: 'Planned',
    in_progress: 'On the Road',
    completed: 'Completed'
  };

  /**
   * Structured route view model for adapters — no HTML/BEM.
   * Optional i18nFn(key) resolves copy; English fallbacks match delivery.js.
   */
  function buildRouteDisplayModel(route, options) {
    options = options || {};
    var i18nFn = options.i18nFn;

    function translate(key, fallback) {
      if (i18nFn) {
        var translated = i18nFn(key);
        if (translated) return translated;
      }
      return fallback;
    }

    if (!route) {
      return {
        routeLabel: '',
        direction: '',
        regionSuffix: '',
        routeStatus: '',
        isStarted: false,
        isCompleted: false,
        spotAvailable: false,
        spotLabelKey: '',
        spotLabel: '',
        statusLabelKey: 'route-planned',
        statusLabel: ROUTE_STATUS_FALLBACK_LABELS.planned,
        etaFormatted: '',
        windowLabelKey: '',
        windowLabel: '',
        stopsLabel: '',
        showSpotBadge: false,
        showSpotsClosed: false
      };
    }

    var isStarted = route.routeStatus === 'in_progress';
    var isCompleted = route.routeStatus === 'completed';
    var spotAvail = !isStarted && !isCompleted && isSpotAvailable(route);
    var statusLabelKey = isStarted
      ? 'route-in-progress'
      : isCompleted
        ? 'route-completed'
        : 'route-planned';
    var statusFallback = isStarted
      ? ROUTE_STATUS_FALLBACK_LABELS.in_progress
      : isCompleted
        ? ROUTE_STATUS_FALLBACK_LABELS.completed
        : ROUTE_STATUS_FALLBACK_LABELS.planned;
    var direction = resolveRegion(route);
    var etaFormatted = isStarted ? formatRouteEta(route.estimatedReturnAt) : '';

    var windowLabelKey = '';
    var windowLabel = '';
    if (
      !isStarted &&
      !isCompleted &&
      route.scheduledStartWindow &&
      SCHEDULED_WINDOW_I18N_KEYS[route.scheduledStartWindow]
    ) {
      windowLabelKey = SCHEDULED_WINDOW_I18N_KEYS[route.scheduledStartWindow];
      windowLabel = translate(windowLabelKey, route.scheduledStartWindow);
    }

    var stopsLabel = '';
    // No stops yet reads better as nothing than as "~0 stops".
    if (route.approxStopCount != null && route.approxStopCount > 0 && !isCompleted) {
      var approxTpl = translate('approx-stops', '~{{ count }} stops');
      stopsLabel = approxTpl.replace('{{ count }}', route.approxStopCount);
    }

    return {
      routeLabel: route.routeLabel || '',
      direction: direction,
      regionSuffix: direction ? ' area' : '',
      routeStatus: route.routeStatus || '',
      isStarted: isStarted,
      isCompleted: isCompleted,
      spotAvailable: spotAvail,
      spotLabelKey: spotAvail ? 'spot-available' : '',
      spotLabel: spotAvail
        ? translate('spot-available', 'Spot Available')
        : '',
      statusLabelKey: statusLabelKey,
      statusLabel: translate(statusLabelKey, statusFallback),
      etaFormatted: etaFormatted,
      windowLabelKey: windowLabelKey,
      windowLabel: windowLabel,
      stopsLabel: stopsLabel,
      showSpotBadge: spotAvail,
      showSpotsClosed: isStarted
    };
  }

  /**
   * What the account overview strip says before the route list is opened: route
   * counts, the in-progress route due back first, and how many spots are open.
   * Open spots prefer the server summary (spotAvailableRouteCount) when it sends one.
   */
  function buildOverviewSummary(routes, summary) {
    var out = { road: 0, planned: 0, done: 0, total: 0, openSpots: 0, nextBack: null };
    var first = null;
    var firstAt = Infinity;
    (routes || []).forEach(function (route) {
      out.total += 1;
      if (route.routeStatus === 'in_progress') {
        out.road += 1;
        var at = Date.parse(route.estimatedReturnAt);
        if (!isNaN(at) && at < firstAt) {
          firstAt = at;
          first = route;
        }
      } else if (route.routeStatus === 'completed') {
        out.done += 1;
      } else {
        out.planned += 1;
      }
      if (isSpotAvailable(route)) out.openSpots += 1;
    });
    if (summary && typeof summary.spotAvailableRouteCount === 'number') {
      out.openSpots = summary.spotAvailableRouteCount;
    }
    if (first) {
      out.nextBack = {
        routeLabel: first.routeLabel || '',
        region: resolveRegion(first),
        eta: formatRouteEta(first.estimatedReturnAt)
      };
    }
    return out;
  }

  function createVisibilityPoller(tickFn, intervalMs, options) {
    options = options || {};
    var doc =
      options.document ||
      (typeof document !== 'undefined' ? document : null);
    var schedule =
      options.setTimeout ||
      (typeof setTimeout !== 'undefined' ? setTimeout : null);
    var cancel =
      options.clearTimeout ||
      (typeof clearTimeout !== 'undefined' ? clearTimeout : null);

    var timer = null;
    var paused = false;

    function tick() {
      tickFn();
      if (schedule) {
        timer = schedule(tick, intervalMs);
      }
    }

    function onVisibility() {
      if (!doc) return;
      if (doc.hidden) {
        if (cancel && timer != null) cancel(timer);
        timer = null;
        paused = true;
      } else if (paused) {
        paused = false;
        tick();
      }
    }

    function start() {
      if (timer != null) return;
      tick();
      if (doc && doc.addEventListener) {
        doc.addEventListener('visibilitychange', onVisibility);
      }
    }

    function stop() {
      if (cancel && timer != null) cancel(timer);
      timer = null;
      paused = false;
      if (doc && doc.removeEventListener) {
        doc.removeEventListener('visibilitychange', onVisibility);
      }
    }

    return { start: start, stop: stop };
  }

  var DEFAULT_PROXY_RETRY_MAX = 50;
  var DEFAULT_PROXY_RETRY_INTERVAL_MS = 300;

  /**
   * Waits for App Proxy client via getProxy(); calls onReady(proxy) or onExhausted once.
   * Matches adapter retry pattern (50 × 300 ms). Injectable timers for Node tests.
   */
  function createProxyRetryScheduler(options) {
    options = options || {};
    var getProxy = options.getProxy;
    var maxRetries =
      options.maxRetries != null ? options.maxRetries : DEFAULT_PROXY_RETRY_MAX;
    var intervalMs =
      options.intervalMs != null
        ? options.intervalMs
        : DEFAULT_PROXY_RETRY_INTERVAL_MS;
    var onReady = options.onReady || function () {};
    var onExhausted = options.onExhausted || function () {};
    var schedule =
      options.setTimeout ||
      (typeof setTimeout !== 'undefined' ? setTimeout : null);
    var cancel =
      options.clearTimeout ||
      (typeof clearTimeout !== 'undefined' ? clearTimeout : null);

    var retries = 0;
    var exhausted = false;
    var timer = null;

    function attempt() {
      var proxy = getProxy ? getProxy() : null;
      if (proxy) {
        retries = 0;
        onReady(proxy);
        return;
      }
      if (retries < maxRetries) {
        retries += 1;
        if (schedule) {
          timer = schedule(attempt, intervalMs);
        }
        return;
      }
      if (!exhausted) {
        exhausted = true;
        onExhausted();
      }
    }

    function start() {
      attempt();
    }

    function stop() {
      if (cancel && timer != null) {
        cancel(timer);
      }
      timer = null;
      retries = 0;
      exhausted = false;
    }

    return { start: start, stop: stop };
  }

  global.BmwCustomerAccountDeliveryCore = {
    ROUTE_DIRECTION_MAP: ROUTE_DIRECTION_MAP,
    getRouteDirection: getRouteDirection,
    resolveRegion: resolveRegion,
    isSpotAvailable: isSpotAvailable,
    sortRoutesByStatus: sortRoutesByStatus,
    formatRouteEta: formatRouteEta,
    formatDeliveredAt: formatDeliveredAt,
    buildRouteDisplayModel: buildRouteDisplayModel,
    buildOverviewSummary: buildOverviewSummary,
    createVisibilityPoller: createVisibilityPoller,
    createProxyRetryScheduler: createProxyRetryScheduler
  };
})(typeof window !== 'undefined' ? window : this);
